// AlertToast — Phase 4 Plan 04-05 (DIS-17, D-74, UI-SPEC §Screen 1).
//
// Manual-dismiss toast for dispatcher alerts (driver_offline_1h, overdue_2h,
// problem_open). Severity colors come from ALERT_SEVERITY_COLORS; aria-live
// is "polite" for warning, "assertive" for destructive per UI-SPEC §Screen
// Reader. Auto-dismiss is explicitly disabled — dispatcher MUST acknowledge.

import { ALERT_SEVERITY_COLORS, type AlertSeverity, TOUCH_TARGET_MIN } from "../lib/tokens";
import { prefersDark } from "../lib/tripStatusBadge";

export interface AlertToastProps {
  severity: AlertSeverity;
  title: string;
  body?: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  /** Optional test id override. */
  "data-testid"?: string;
}

export function AlertToast({
  severity,
  title,
  body,
  actionLabel,
  onAction,
  onDismiss,
  ...rest
}: AlertToastProps): React.JSX.Element {
  const palette = ALERT_SEVERITY_COLORS[severity];
  const testId = rest["data-testid"] ?? `alert-toast-${severity}`;
  const ariaLive = severity === "destructive" ? "assertive" : "polite";

  // Dark-aware severity surface (UIR-05, threat T-12-11-02): pick bgDark/textDark
  // when the WebView appearance is dark so warning/destructive stay readable.
  // border stays the same severity accent in both themes (per token).
  const dark = prefersDark();
  const bg = dark ? palette.bgDark : palette.bg;
  const text = dark ? palette.textDark : palette.text;

  return (
    <div
      role="alert"
      aria-live={ariaLive}
      data-testid={testId}
      data-severity={severity}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: 12,
        background: bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        color: text,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: body ? 4 : 0 }}>{title}</div>
        {body ? <div style={{ fontSize: 13, lineHeight: 1.4 }}>{body}</div> : null}
      </div>

      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          data-testid={`${testId}-action`}
          style={{
            minHeight: TOUCH_TARGET_MIN,
            padding: "6px 12px",
            background: palette.border,
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {actionLabel}
        </button>
      ) : null}

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss alert"
          data-testid={`${testId}-dismiss`}
          style={{
            minHeight: TOUCH_TARGET_MIN,
            minWidth: TOUCH_TARGET_MIN,
            background: "transparent",
            border: "none",
            color: text,
            fontSize: 18,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
